import { useEffect, useState } from "react";
import {
  UserFormat,
  UserCredentials,
} from "../../common/models/schema/UserFormat";
import { ApiError } from "../../common/api/ApiClient";
import { useApiClient } from "../../common/hooks/useApiClient";
import { UserPermissionGroup } from "../../common/models/schema/UserPermissionGroup";

export const useUsersManager = () => {
  const [users, setUsers] = useState<UserFormat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const apiClient = useApiClient();
  const basePath = "/auth";

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ users: UserFormat[] }>(
        basePath + "/get-all-users",
      );
      const users = response.data?.users;
      setUsers(users);
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (user: UserCredentials) => {
    try {
      setCreateUserError(null);
      await apiClient.post<UserFormat>(basePath + "/sign-up", user);
      const newUserData = {
        email: user.email,
        groups: UserPermissionGroup.Default,
      };
      setUsers([...users, newUserData as UserFormat]);
    } catch (error) {
      const apiError = error as ApiError;
      setCreateUserError(apiError.data?.detail.message || apiError.message);
      throw apiError;
    }
  };

  const changeUserPermissionGroup = async (
    email: string,
    group: UserPermissionGroup,
  ) => {
    try {
      setLoading(true);
      await apiClient.put<UserFormat>(basePath + "/set-group", {
        email: email,
        groups: group,
      });
      setUsers(
        users.map((user: UserFormat) =>
          user.email === email
            ? { ...user, groups: group }
            : (user as UserFormat),
        ),
      );
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.data?.detail.message || apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (email: string) => {
    try {
      setLoading(true);
      await apiClient.post<UserFormat>(basePath + "/delete-user", {
        email: email,
      });
      setUsers(users.filter((user: UserFormat) => user.email !== email));
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.data?.detail.message || apiError.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    users,
    setUsers,
    loading,
    error,
    getUsers,
    createUser,
    createUserError,
    changeUserPermissionGroup,
    deleteUser,
  };
};
